import { useState } from 'react';
import { Banner, Button, Card, DataTable, Pill, Spinner, Td } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { useAsync } from '../lib/useAsync';

interface EarlyAccessRow {
  id: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  state: string;
  county: string | null;
  role: string | null;
  jobsPerYear: string | null;
  note: string | null;
  source: string | null;
  createdAt: string;
  approvedAt: string | null;
}

/** Marcus's queue: approve puts the email on the pilot allowlist and sends the sign-in steps. */
export default function AdminEarlyAccess() {
  const { data, loading, error, reload } = useAsync(() => api.get<{ requests: EarlyAccessRow[] }>('/api/admin/early-access'), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  if (loading && !data) return <Spinner label="Loading requests…" />;
  if (error) return <Banner tone="urgent" title="Not available">{error}</Banner>;
  const rows = data?.requests ?? [];

  const approve = async (id: string) => {
    setBusy(id);
    setProblem(null);
    try {
      await api.post(`/api/admin/early-access/${id}/approve`, {});
      await reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : 'Approval failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink">Early access requests</h1>
        <p className="text-sm text-muted">
          Approve adds the email to the pilot and emails them how to sign in. Outside Florida is the waitlist: no approval needed.
        </p>
      </div>
      {problem && <Banner tone="urgent">{problem}</Banner>}
      <Card>
        <DataTable head={['Who', 'Where', 'Work', 'From', 'Asked', '']} empty="No requests yet.">
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border/60 align-top">
              <Td>
                <p className="font-medium">{r.name}{r.company ? `, ${r.company}` : ''}</p>
                <p className="text-xs text-muted">{r.email}{r.phone ? ` · ${r.phone}` : ''}</p>
                {r.note && <p className="mt-1 text-xs text-dim">“{r.note}”</p>}
              </Td>
              <Td className="text-muted">{r.state}{r.county ? ` · ${r.county}` : ''}</Td>
              <Td className="text-muted">{[r.role, r.jobsPerYear && `${r.jobsPerYear} jobs/yr`].filter(Boolean).join(' · ') || '—'}</Td>
              <Td className="text-xs text-dim">{r.source ?? '—'}</Td>
              <Td className="text-xs text-muted">{fmtDateTime(r.createdAt)}</Td>
              <Td>
                {r.state !== 'FL' ? (
                  <Pill>Waitlist</Pill>
                ) : r.approvedAt ? (
                  <Pill tone="ok">Approved</Pill>
                ) : (
                  <Button disabled={busy === r.id} onClick={() => void approve(r.id)}>
                    Approve
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </DataTable>
      </Card>
    </div>
  );
}
